/**
 * Authenticated Axios instance.
 *
 * - Request interceptor: attaches Bearer token from localStorage
 * - Response interceptor: on 401, refreshes the token (with dedup),
 *   retries the original request, or redirects to /auth/login
 *
 * Usage:
 *   import apiClient from 'api/apiClient';
 *   const res = await apiClient.get('/clients/2/contacts');
 */
import axios from 'axios';

import { backend_url as backendUrl } from '../config/variables';

// ── Create instance ──────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: backendUrl,
  headers: { Accept: 'application/json' },
});

// ── Refresh deduplication ────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// ── Auth helpers ─────────────────────────────────────────────────────────────
const AUTH_KEYS = ['token', 'refreshToken', 'successStatus'];

const clearAuth = () => AUTH_KEYS.forEach((k) => localStorage.removeItem(k));

const redirectToLogin = () => {
  clearAuth();
  window.location.href = '/auth/login';
};

// ── Request interceptor ──────────────────────────────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Maintenance mode state ───────────────────────────────────────────────────
let maintenanceCallback = null;

export const onMaintenance = (cb) => {
  maintenanceCallback = cb;
};

// ── Response interceptor ─────────────────────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Maintenance mode — backend returned 503 with maintenance flag
    if (
      error.response &&
      error.response.status === 503 &&
      error.response.data?.maintenance
    ) {
      if (maintenanceCallback) maintenanceCallback();
      return Promise.reject(error);
    }

    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    const currentRefresh = localStorage.getItem('refreshToken');
    if (!currentRefresh) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // If a refresh is already in flight, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        originalRequest._retry = true;
        return apiClient(originalRequest);
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;

    try {
      // Use raw axios for refresh to avoid interceptor loop
      const { data } = await axios.post(
        `${backendUrl}/refresh`,
        {},
        { headers: { Authorization: `Bearer ${currentRefresh}` } },
      );

      if (!data?.access_token) throw new Error('Invalid refresh response');

      localStorage.setItem('token', data.access_token);
      processQueue(null, data.access_token);

      originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      if (
        refreshError.response &&
        (refreshError.response.status === 401 || refreshError.response.status === 422)
      ) {
        redirectToLogin();
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
